// src/stores/attendance.js - Enhanced with Synchronized Timing
import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import attendanceService from '@/services/attendance';
import dayjs from 'dayjs';

export const useAttendanceStore = defineStore('attendance', () => {
  // State
  const todayAttendance = ref(null);
  const attendanceHistory = ref([]);
  const monthlyAttendance = ref({});
  const isLoading = ref(false);
  const error = ref(null);

  // Enhanced timing state
  const currentTime = ref(new Date());
  const workStartTime = ref(null);
  const workDurationTimer = ref(null);
  const liveDurationSeconds = ref(0);

  // Current session tracking
  const currentSession = ref({
    isActive: false,
    startTime: null,
    duration: 0,
    locationUpdates: [],
  });

  // Statistics
  const statistics = ref({
    totalDays: 0,
    totalHours: 0,
    averageHours: 0,
    lateCount: 0,
    earlyLeaveCount: 0,
    fieldDays: 0,
  });

  // Computed properties
  const hasClockIn = computed(() => {
    return todayAttendance.value?.clock_in_time !== null;
  });

  const hasClockOut = computed(() => {
    return todayAttendance.value?.clock_out_time !== null;
  });

  const isActive = computed(() => {
    return hasClockIn.value && !hasClockOut.value;
  });

  const isWorkingNow = computed(() => {
    return isActive.value;
  });

  const clockInTime = computed(() => {
    if (!todayAttendance.value?.clock_in_time) return null;
    return todayAttendance.value.clock_in_time;
  });

  const clockOutTime = computed(() => {
    if (!todayAttendance.value?.clock_out_time) return null;
    return todayAttendance.value.clock_out_time;
  });

  // Live work duration calculation (updates every second)
  const currentWorkDuration = computed(() => {
    if (!isActive.value || !clockInTime.value) {
      return '0h 0m';
    }

    const start = parseTimeToToday(clockInTime.value);
    const now = currentTime.value;
    const diff = now - start;

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m`;
  });

  // Live work duration with seconds (for detailed displays)
  const liveWorkDuration = computed(() => {
    if (!isActive.value || !clockInTime.value) {
      return '0h 0m 0s';
    }

    const start = parseTimeToToday(clockInTime.value);
    const now = currentTime.value;
    const diff = now - start;

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return `${hours}h ${minutes}m ${seconds}s`;
  });

  // Duration in minutes for calculations
  const currentWorkMinutes = computed(() => {
    if (!isActive.value || !clockInTime.value) {
      return 0;
    }

    const start = parseTimeToToday(clockInTime.value);
    const now = currentTime.value;
    const diff = now - start;

    return Math.floor(diff / (1000 * 60));
  });

  // Today's work duration (completed or in progress)
  const todayWorkDuration = computed(() => {
    if (hasClockOut.value && todayAttendance.value?.work_duration) {
      // Use stored duration if clocked out
      return formatDurationFromMinutes(todayAttendance.value.work_duration);
    } else if (isActive.value) {
      // Use live duration if currently working
      return currentWorkDuration.value;
    }
    return '0h 0m';
  });

  // Helper functions
  const parseTimeToToday = timeString => {
    if (!timeString) return new Date();

    // Handle ISO timestamps (e.g. "2026-02-14T23:56:22.000Z")
    // DB stores Palestine time but mysql2 appends Z suffix - treat as raw time
    if (timeString.includes('T')) {
      // Extract the time portion and treat it as-is (Palestine time)
      const timePart = timeString.split('T')[1].replace('Z', '').split('.')[0];
      const [hours, minutes, seconds = '00'] = timePart.split(':');
      const today = new Date();
      return new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        parseInt(hours, 10),
        parseInt(minutes, 10),
        parseInt(seconds, 10)
      );
    }

    // Handle both "HH:MM:SS" and "HH:MM" formats
    const today = new Date();
    const [hours, minutes, seconds = '00'] = timeString.split(':');

    return new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      parseInt(hours, 10),
      parseInt(minutes, 10),
      parseInt(seconds, 10)
    );
  };

  const formatDurationFromMinutes = minutes => {
    if (!minutes || minutes === 0) return '0h 0m';

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    return `${hours}h ${remainingMinutes}m`;
  };

  const formatTime = timeString => {
    if (!timeString) return 'N/A';

    // Handle both full datetime and time-only strings
    try {
      const time = new Date(`2000-01-01 ${timeString}`);
      return time.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    } catch {
      return timeString;
    }
  };

  const getTimeAgo = timeString => {
    if (!timeString) return '';

    const clockInTime = parseTimeToToday(timeString);
    const now = currentTime.value;
    const diff = now - clockInTime;

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m ago`;
    }
    return `${minutes}m ago`;
  };

  // Timer management
  const startTimer = () => {
    stopTimer(); // Ensure no duplicate timers

    // Update current time every second
    workDurationTimer.value = setInterval(() => {
      currentTime.value = new Date();

      // Update live duration seconds for additional granularity
      if (isActive.value && clockInTime.value) {
        const start = parseTimeToToday(clockInTime.value);
        const now = currentTime.value;
        const diff = now - start;
        liveDurationSeconds.value = Math.floor(diff / 1000);
      }
    }, 1000);
  };

  const stopTimer = () => {
    if (workDurationTimer.value) {
      clearInterval(workDurationTimer.value);
      workDurationTimer.value = null;
    }
  };

  // Actions
  const fetchTodayAttendance = async () => {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await attendanceService.getTodayAttendance();
      todayAttendance.value = response.data;

      // Update session state
      if (isWorkingNow.value) {
        currentSession.value.isActive = true;
        currentSession.value.startTime = todayAttendance.value.clock_in_time;
        workStartTime.value = todayAttendance.value.clock_in_time;

        // Start timer if currently working
        startTimer();
      } else {
        currentSession.value.isActive = false;
        stopTimer();
      }

      return response.data;
    } catch (err) {
      error.value = err.message;
      console.error('Failed to fetch today attendance:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchAttendanceHistory = async (filters = {}) => {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await attendanceService.getAttendanceHistory(filters);
      attendanceHistory.value = response.data.records;
      statistics.value = response.data.statistics;

      return response.data;
    } catch (err) {
      error.value = err.message;
      console.error('Failed to fetch attendance history:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchMonthlyAttendance = async (year, month) => {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await attendanceService.getMonthlyAttendance(
        year,
        month
      );
      monthlyAttendance.value = response.data;

      return response.data;
    } catch (err) {
      error.value = err.message;
      console.error('Failed to fetch monthly attendance:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const clockIn = async (photoData, locationData) => {
    try {
      isLoading.value = true;
      error.value = null;

      // Validate inputs
      if (!photoData || !photoData.blob) {
        throw new Error('Photo is required for clock in');
      }

      if (!locationData || !locationData.latitude) {
        throw new Error('Location is required for clock in');
      }

      // Check if already clocked in
      if (isWorkingNow.value) {
        throw new Error('Already clocked in. Please clock out first.');
      }

      // Upload photo and clock in
      const response = await attendanceService.clockIn(
        photoData.blob,
        locationData
      );

      // Update store
      todayAttendance.value = response.data.attendance;
      workStartTime.value = response.data.attendance.clock_in_time;

      currentSession.value = {
        isActive: true,
        startTime: response.data.attendance.clock_in_time,
        duration: 0,
        locationUpdates: [locationData],
      };

      // Start timer for live duration tracking
      startTimer();

      // Start location tracking if field mode
      if (locationData.mode === 'field') {
        startLocationTracking();
      }

      return response.data;
    } catch (err) {
      error.value = err.message;
      console.error('Clock in failed:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const clockInWithLeaveConfirmation = async (photoData, locationData) => {
    try {
      isLoading.value = true;
      error.value = null;

      // Validate inputs
      if (!photoData || !photoData.blob) {
        throw new Error('Photo is required for clock in');
      }

      if (!locationData || !locationData.latitude) {
        throw new Error('Location is required for clock in');
      }

      if (!locationData.leaveId) {
        throw new Error('Leave ID is required for confirmation');
      }

      // Upload photo and clock in with leave confirmation
      const response = await attendanceService.clockInWithLeaveConfirmation(
        photoData.blob,
        locationData
      );

      // Update store
      todayAttendance.value = response.data.attendance;
      workStartTime.value = response.data.attendance.clock_in_time;

      currentSession.value = {
        isActive: true,
        startTime: response.data.attendance.clock_in_time,
        duration: 0,
        locationUpdates: [locationData],
      };

      // Start timer for live duration tracking
      startTimer();

      // Start location tracking if field mode
      if (locationData.mode === 'field') {
        startLocationTracking();
      }

      return response.data;
    } catch (err) {
      error.value = err.message;
      console.error('Clock in with leave confirmation failed:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const clockOut = async (photoData, locationData) => {
    try {
      isLoading.value = true;
      error.value = null;

      // Validate inputs
      if (!photoData || !photoData.blob) {
        throw new Error('Photo is required for clock out');
      }

      if (!locationData || !locationData.latitude) {
        throw new Error('Location is required for clock out');
      }

      // Check if clocked in
      if (!isWorkingNow.value) {
        throw new Error('Not clocked in. Please clock in first.');
      }

      // Upload photo and clock out
      const response = await attendanceService.clockOut(
        photoData.blob,
        locationData
      );

      // Update store
      todayAttendance.value = response.data.attendance;

      currentSession.value = {
        isActive: false,
        startTime: null,
        duration: response.data.attendance.work_duration,
        locationUpdates: [],
      };

      // Stop timer and location tracking
      stopTimer();
      stopLocationTracking();

      return response.data;
    } catch (err) {
      error.value = err.message;
      console.error('Clock out failed:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Break management
  const startBreak = async (reason = 'Regular break') => {
    try {
      isLoading.value = true;
      error.value = null;

      if (!isWorkingNow.value) {
        throw new Error('Not clocked in. Cannot start break.');
      }

      const response = await attendanceService.startBreak(reason);

      // Update attendance record to reflect break status
      if (todayAttendance.value) {
        todayAttendance.value.is_on_break = true;
        todayAttendance.value.current_break_start = new Date().toISOString();
      }

      return response.data;
    } catch (err) {
      error.value = err.message;
      console.error('Start break failed:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const endBreak = async () => {
    try {
      isLoading.value = true;
      error.value = null;

      if (!isWorkingNow.value) {
        throw new Error('Not clocked in. Cannot end break.');
      }

      const response = await attendanceService.endBreak();

      // Update attendance record
      if (todayAttendance.value) {
        todayAttendance.value.is_on_break = false;
        todayAttendance.value.current_break_start = null;
        todayAttendance.value.break_duration = response.data.totalBreakTime || 0;
      }

      return response.data;
    } catch (err) {
      error.value = err.message;
      console.error('End break failed:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Get current attendance status
  const getAttendanceStatus = async () => {
    try {
      const response = await attendanceService.getStatus();
      return response.data;
    } catch (err) {
      console.error('Get status failed:', err);
      return null;
    }
  };

  const updateLocation = async locationData => {
    try {
      if (!isWorkingNow.value) {
        return;
      }

      // Add to session updates
      currentSession.value.locationUpdates.push({
        ...locationData,
        timestamp: new Date().toISOString(),
      });

      // Send to server (throttled)
      await attendanceService.updateLocation(locationData);
    } catch (err) {
      console.error('Location update failed:', err);
      // Don't throw - location updates shouldn't break the app
    }
  };

  // Location tracking
  let locationInterval = null;

  const startLocationTracking = () => {
    // Update location every 5 minutes
    locationInterval = setInterval(() => {
      if (isWorkingNow.value && window.navigator.geolocation) {
        window.navigator.geolocation.getCurrentPosition(
          position => {
            updateLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
            });
          },
          error => {
            console.error('Location tracking error:', error);
          }
        );
      }
    }, 5 * 60 * 1000); // 5 minutes
  };

  const stopLocationTracking = () => {
    if (locationInterval) {
      clearInterval(locationInterval);
      locationInterval = null;
    }
  };

  // Calculate work time for a date range
  const calculateWorkTime = (startDate, endDate) => {
    const filtered = attendanceHistory.value.filter(record => {
      const date = dayjs(record.created_at);
      return date.isAfter(startDate) && date.isBefore(endDate);
    });

    const totalMinutes = filtered.reduce((sum, record) => {
      return sum + (record.work_duration || 0);
    }, 0);

    return {
      totalMinutes,
      totalHours: Math.floor(totalMinutes / 60),
      averageHours:
        filtered.length > 0 ? totalMinutes / filtered.length / 60 : 0,
    };
  };

  // Export attendance data
  const exportAttendance = async (format = 'csv', filters = {}) => {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await attendanceService.exportAttendance(
        format,
        filters
      );

      // Create download
      const blob = new Blob([response.data], {
        type: format === 'csv' ? 'text/csv' : 'application/pdf',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attendance_${dayjs().format('YYYY-MM-DD')}.${format}`;
      link.click();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      error.value = err.message;
      console.error('Export failed:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Initialize store (start timer if needed)
  const initialize = async () => {
    try {
      // Start the global timer for current time updates
      startTimer();

      // Fetch today's attendance to sync state
      await fetchTodayAttendance();
    } catch (err) {
      console.error('Failed to initialize attendance store:', err);
    }
  };

  // Reset store
  const reset = () => {
    todayAttendance.value = null;
    attendanceHistory.value = [];
    monthlyAttendance.value = {};
    workStartTime.value = null;
    liveDurationSeconds.value = 0;

    currentSession.value = {
      isActive: false,
      startTime: null,
      duration: 0,
      locationUpdates: [],
    };

    statistics.value = {
      totalDays: 0,
      totalHours: 0,
      averageHours: 0,
      lateCount: 0,
      earlyLeaveCount: 0,
      fieldDays: 0,
    };

    error.value = null;
    stopTimer();
    stopLocationTracking();
  };

  // Watch for changes in work status to manage timer
  watch(isActive, newValue => {
    if (newValue) {
      startTimer();
    } else {
      stopTimer();
    }
  });

  return {
    // State
    todayAttendance,
    attendanceHistory,
    monthlyAttendance,
    isLoading,
    error,
    currentSession,
    statistics,
    currentTime,
    workStartTime,
    liveDurationSeconds,

    // Computed
    hasClockIn,
    hasClockOut,
    isActive,
    isWorkingNow,
    clockInTime,
    clockOutTime,
    currentWorkDuration,
    liveWorkDuration,
    currentWorkMinutes,
    todayWorkDuration,

    // Actions
    fetchTodayAttendance,
    fetchAttendanceHistory,
    fetchMonthlyAttendance,
    clockIn,
    clockInWithLeaveConfirmation,
    clockOut,
    startBreak,
    endBreak,
    getAttendanceStatus,
    updateLocation,
    calculateWorkTime,
    exportAttendance,
    initialize,
    reset,
    startTimer,
    stopTimer,

    // Helper functions
    formatTime,
    getTimeAgo,
    formatDurationFromMinutes,
  };
});
